import { DialogContent } from '@mui/material'

import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingCreationRequest,
  useDigitalTwin,
} from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel, DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { BASE_SETTING_PRIORITIES, DEVIATION_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import { applySystemDisplayColors } from 'helpers/settingPriority.helper/settingPriority.helper'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { Dialog } from 'ui/components/Dialog/Dialog'
import PreviousSettingsTable from 'ui/components/DigitalTwinEditSettingsModal/PreviousSettingsTable/PreviousSettingsTable'
import { DigitalTwinSettingDialog } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialog'
import { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { useConfirmedClose } from 'ui/components/DigitalTwinSettingEditor/useConfirmedClose'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SelectButton from 'ui/components/SelectButton/SelectButton'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { getPropertiesFromDigitalTwinModel } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import {
  getPrettyName,
  getSettingsWithUnitsFromMatchingConfigurableProperty,
} from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import {
  EventPost,
  isTimeSeriesSettingInstruction,
  SettingOrTimeSeriesInstructions,
} from 'views/EventsView/components/events.helper'
import styles from './EventSettingsOverviewTable.module.less'

type EventSettingsOverviewTableProps = {
  level?: string | undefined
  attribute?: string
  name?: string
  index: number
  eventPost?: EventPost
  settings?: SettingOrTimeSeriesInstructions[]
  setSettingInstructionsData?: (data: DigitalTwinSettingCreationRequest) => void
}

// NOTE: If you make changes to this file it should probably be changed in ChangeRequestSettingInstructionOverviewTable.tsx as well
export default function EventSettingsInstructionsOverviewTable({
  level,
  attribute,
  name,
  index,
  eventPost,
  settings,
  setSettingInstructionsData,
}: EventSettingsOverviewTableProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem, hasAccess, user } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const [openCreateSettingModal, setOpenCreateSettingModal] = useState(false)
  const [openEditSettingModal, setOpenEditSettingModal] = useState(false)
  const [initData, setInitData] = useState<null | CreateSettingData>(null)
  const closeSettingModal = (): void => {
    setInitData(null)
    openCreateSettingModal ? setOpenCreateSettingModal(false) : setOpenEditSettingModal(false)
  }
  const { requestClose, onUnsavedChangesChange, closeConfirmDialog } = useConfirmedClose(closeSettingModal)

  const canUpdateActiveEvent = eventPost?.permitted_actions?.includes('update')

  const noSettingWritePermission = eventPost?.setting_instructions?.linked_objects?.[index]?.write === false

  const disableSettings = !canUpdateActiveEvent || noSettingWritePermission

  const priorities = applySystemDisplayColors(DEVIATION_SETTING_PRIORITIES, activeSystem?.setting_priority_levels)
  const defaultDeviationPriorityLevel = priorities[0].priority_level

  const userName = `${user?.first_name} ${user?.last_name}`

  const eventId = eventPost?.id

  const onClickSelectButtonForSetting = useCallback(() => {
    setInitData({
      level: level as DeviationSettingLevel,
      name: name,
      attribute: attribute,
      start_time: eventPost?.start_time,
      end_time: eventPost?.end_time,
      priority: defaultDeviationPriorityLevel,
      isTimeSeries: false,
      operational_event: eventId ?? null,
      creator_name: userName ?? undefined,
    })
    setOpenCreateSettingModal(true)
  }, [
    attribute,
    defaultDeviationPriorityLevel,
    eventPost?.end_time,
    eventId,
    eventPost?.start_time,
    level,
    name,
    userName,
  ])

  const onClickSelectButtonForTimeSeriesSetting = useCallback(() => {
    setInitData({
      level: level as DeviationSettingLevel,
      name: name,
      attribute: attribute,
      start_time: eventPost?.start_time,
      end_time: eventPost?.end_time,
      value: [],
      priority: defaultDeviationPriorityLevel,
      isTimeSeries: true,
      operational_event: eventId ?? null,
      creator_name: userName ?? undefined,
    })
    setOpenCreateSettingModal(true)
  }, [
    attribute,
    defaultDeviationPriorityLevel,
    eventPost?.end_time,
    eventId,
    eventPost?.start_time,
    level,
    name,
    userName,
  ])

  const settingsForChosenPropertyWithUnits = getSettingsWithUnitsFromMatchingConfigurableProperty(
    digitalTwin,
    settings ?? []
  )
  const hasAccessToTimeseries = hasAccess({ submodule: 'optimizesettings_time_series', module: 'events' })

  const onEditSettingInstruction = useCallback(
    (setting: SettingOrTimeSeriesInstructions | DigitalTwinSetting): void => {
      const isTimeSeries = isTimeSeriesSettingInstruction(setting)
      const value = isTimeSeries
        ? 'values' in setting
          ? setting.values?.map((value: number | null) => ({ value: value }))
          : []
        : 'value' in setting
          ? typeof setting.value === 'string'
            ? Number(setting.value)
            : setting.value
          : null

      setInitData({
        level: setting.level as DeviationSettingLevel,
        name: setting.name,
        attribute: setting.attribute,
        priority: setting.priority,
        comment: setting.comment,
        value: value,
        isTimeSeries: isTimeSeries,
        operational_event: eventId ?? null,
        id: setting.id ?? undefined,
        creator_name: setting.creator_name ?? undefined,
        start_time: (setting.start_time as `${number}-${number}-${number}T${number}:${number}:${number}+00:00`) ?? null,
        end_time: (setting.end_time as `${number}-${number}-${number}T${number}:${number}:${number}+00:00`) ?? null,
      })
      setOpenEditSettingModal(true)
    },
    [eventId]
  )

  const allConfigurableProperties: ConfigurableProperty[] = useMemo(
    () => (digitalTwin?.model ? getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model) : []),
    [digitalTwin]
  )

  const disableCreateSettingButton =
    level === '' ||
    name === '' ||
    attribute === '' ||
    level === undefined ||
    name === undefined ||
    attribute === undefined

  return (
    <>
      <div className={styles.EventSettingsOverviewTable_CreateSettingsButton}>
        {eventPost?.status !== 'Active' && settingsForChosenPropertyWithUnits.length > 0 && (
          <InfoBanner text={t('Settings are currently inactive. Activate event to activate settings')} style="info" />
        )}

        {!canUpdateActiveEvent || disableSettings ? (
          <></>
        ) : hasAccess({ submodule: 'optimizesettings_time_series', module: 'events' }) ? (
          <SelectButton
            disabled={disableCreateSettingButton}
            icon="fal fa-plus"
            primary
            data-testid="create-setting-button" // e2e: create new setting trigger — do not remove
            menuItems={[
              {
                label: t('Setting'),
                onClick: onClickSelectButtonForSetting,
                'data-testid': 'create-setting-option', // e2e: "Setting" menu option — do not remove
              },
              {
                label: t('Time series setting'),
                onClick: onClickSelectButtonForTimeSeriesSetting,
              },
            ]}
          >
            {t(`Create new setting`)}
          </SelectButton>
        ) : (
          <Button
            primary
            onClick={onClickSelectButtonForSetting}
            disabled={disableCreateSettingButton}
            data-testid="create-setting-button" // e2e: create new setting trigger — do not remove
          >
            {t(`Create new setting`)}
          </Button>
        )}
      </div>
      <PreviousSettingsTable
        allSettingsForChosenProperty={settingsForChosenPropertyWithUnits}
        onEditSetting={onEditSettingInstruction}
        permissions={{
          //Set always to true so user is able to view the setting (good to see e.g. graph for timeseries settings). All fields are disabled if user doesn't have permission
          canEdit: true,
          canEditHistoric: true,
        }}
        priorities={priorities}
        eventPost={{
          isOperationalEvent: true,
          eventId: eventId ?? null,
          linked_objects: eventPost?.setting_instructions?.linked_objects ?? null,
          index: index,
        }}
      />
      <Dialog open={openCreateSettingModal || openEditSettingModal} fullWidth maxWidth="md">
        <CustomDialogTitle
          title={`${getPrettyName(`${level}.${name}`, digitalTwin?.translations)}: ${getPrettyName(`${level}.${name}.${attribute}`, digitalTwin?.translations)}`}
          handleClose={requestClose}
        />
        <DialogContent>
          <div>
            {initData?.isTimeSeries && !hasAccessToTimeseries && (
              <InfoBanner text={t('Unfortunately, you do not have permission to make changes here')} style="warning" />
            )}
            <DigitalTwinSettingDialog
              propertyItems={settings ?? []}
              digitalTwin={digitalTwin ?? ({} as DigitalTwin)}
              allowEmpty={false}
              priorities={priorities}
              baseSettingPriorities={BASE_SETTING_PRIORITIES}
              operationalEvent={eventPost}
              initData={initData}
              configurableProperties={allConfigurableProperties}
              isBaseSettings={false}
              hasUnitAndProperty={true}
              disable={disableSettings}
              onClose={closeSettingModal}
              onUnsavedChangesChange={onUnsavedChangesChange}
              onRequestClose={requestClose}
              setSettingInstructionsData={setSettingInstructionsData}
            />
          </div>
        </DialogContent>
        {closeConfirmDialog}
      </Dialog>
    </>
  )
}
